<template>
  <div class="personnel-movement-chart">
    <div class="chart-header">
      <h4 class="chart-title">{{ title }}</h4>
      <p class="chart-subtitle">{{ subtitle }}</p>
    </div>
    <div class="chart-container">
      <Bar :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Chart.js 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const props = defineProps({
  title: {
    type: String,
    default: '분기별 인력 변동'
  },
  subtitle: {
    type: String,
    default: '신규 입사 및 퇴사 추이'
  },
  movementData: {
    type: Array,
    required: true
  }
});

// 데이터 정렬 (날짜순)
const sortedData = computed(() => {
  return [...props.movementData].sort((a, b) => {
    return a.period.localeCompare(b.period);
  });
});

// 차트 데이터
const chartData = computed(() => {
  const labels = sortedData.value.map(item => item.period);
  
  const personnelCounts = sortedData.value.map(item => item.personnel_count);
  const newEntries = sortedData.value.map(item => 
    Array.isArray(item.new_entry) ? item.new_entry.length : 0
  );
  const resignations = sortedData.value.map(item => 
    Array.isArray(item.resignation) ? item.resignation.length : 0
  );

  return {
    labels,
    datasets: [
      {
        label: '신규 입사',
        data: newEntries,
        backgroundColor: '#22d3ee',
        borderColor: '#22d3ee',
        borderWidth: 1
      },
      {
        label: '총 인원',
        data: personnelCounts,
        backgroundColor: '#22c55e',
        borderColor: '#22c55e',
        borderWidth: 1
      },
      {
        label: '퇴사',
        data: resignations,
        backgroundColor: '#ef4444',
        borderColor: '#ef4444',
        borderWidth: 1
      }
    ]
  };
});

// 차트 옵션
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        color: '#e5e7eb',
        font: {
          size: 12,
          family: 'Pretendard, -apple-system, sans-serif'
        },
        padding: 15,
        usePointStyle: true,
        pointStyle: 'rect'
      }
    },
    tooltip: {
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      titleColor: '#fff',
      bodyColor: '#e5e7eb',
      borderColor: '#374151',
      borderWidth: 1,
      padding: 12,
      displayColors: true,
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          label += context.parsed.y + '명';
          return label;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
        drawBorder: false
      },
      ticks: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        },
        stepSize: 5,
        callback: function(value) {
          return value + '명';
        }
      }
    }
  }
}));
</script>

<style scoped>
.personnel-movement-chart {
  background: #1f2937;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
}

.chart-header {
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0 0 4px 0;
}

.chart-subtitle {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.chart-container {
  height: 300px;
  position: relative;
}
</style>





